import ArticleLayout from '../../components/ArticleLayout';

export default function ElRadiatorVarmerIkke() {
  return (
    <ArticleLayout
      title="El-radiatoren varmer ikke: Termostat, sikring og overophedning"
      metaTitle="El radiator varmer ikke? Fejlfinding på elvarme"
      metaDescription="Varmer din el-radiator ikke? Tjekliste til sikringsgruppe, HFI/HPFI-relæ, overophedningssikring og elektronisk termostat. Se hvornår den skal udskiftes."
      canonicalPath="/el-radiator-varmer-ikke"
      introLead="Når en el-radiator ikke vil tænde eller varme, ligger årsagen næsten altid i den elektriske forsyning, en udløst overophedningssikring eller en defekt termostatføler."
      introAnswer="I modsætning til vandbårne radiatorer er en el-radiator et rent elektrisk apparat uden vand, luft eller ventilstifter. Start derfor altid med at kontrollere sikringsgruppen og fejlstrømsafbryderen i el-tavlen, før du undersøger radiatorens egne betjeningsknapper."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Fejlfinding på el-radiator' },
        { id: 'el-tavle', title: '2. Sikringsgruppe og HPFI-relæ' },
        { id: 'overhedning', title: '3. Den indbyggede overophedningssikring' },
        { id: 'termostat-fejl', title: '4. Elektronisk vs. bimetal termostat' },
        { id: 'udskiftning', title: '5. Hvornår er el-radiatoren udtjent?' },
        { id: 'faq', title: '6. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 5 sikre trin til kontrol af el-radiator"
      checklist={[
        {
          title: 'Tjek automatsikringer og HPFI-relæ i boligens el-tavle',
          description: 'Undersøg om sikringsgruppen for rummet er slået fra, eller om fejlstrømsafbryderen har afbrudt strømmen.',
        },
        {
          title: 'Kontrollér stikkontakt eller vægafbryder',
          description: 'Er radiatoren tilsluttet en stikkontakt med afbryder, skal kontakten stå på tændt, og stikket sidde helt i bund.',
        },
        {
          title: 'Fjern tøj, gardiner og genstande på radiatoren',
          description: 'Genstande over radiatoren udløser den termiske termosikring, som øjeblikkeligt afbryder varmelegemet.',
        },
        {
          title: 'Nulstil overophedningssikringen hvis radiatoren har en knap',
          description: 'Sluk for strømmen i 15 minutter, eller tryk på en eventuel fysisk reset-knap på bagsiden eller bunden.',
        },
        {
          title: 'Drej termostaten op på maksimum og lyt efter klik',
          description: 'Høres et svagt mekanisk klik ved temperaturregulering, indikerer det, at termostatkontakten reagerer.',
        },
      ]}
      sections={[
        {
          id: 'el-tavle',
          title: 'Tjek el-tavlen: Sikringer og overbelastning',
          content: (
            <>
              <p>
                En el-radiator trækker typisk mellem <span className="font-mono">1.000 W</span> og <span className="font-mono">2.000 W</span> ved fuld belastning. Hvis to el-radiatorer, en elkedel eller en støvsuger kører på samme <span className="font-mono">10 A</span> eller <span className="font-mono">13 A</span> sikringsgruppe, kan den samlede strøm overstige sikringens grænse.
              </p>
              <p>
                Gå til din el-tavle og tjek, om automatsikringen er vippet ned. Hvis den slår fra igen, så snart radiatoren tændes, kan der være en intern kortslutning eller overgang i varmelegemet.
              </p>
            </>
          ),
        },
        {
          id: 'overhedning',
          title: 'Den indbyggede overophedningssikring',
          content: (
            <>
              <p>
                Alle godkendte el-radiatorer er forsynet med en sikkerhedstermostat, der forhindrer brandfare, hvis radiatoren tildækkes af fx et håndklæde, et gardin eller et tørrestativ.
              </p>
              <p>
                Når den interne temperatur overstiger en fastsat sikkerhedsgrænse, kobler overophedningssikringen fra. På nogle modeller nulstilles sikringen automatisk, når apparatet er kølet helt ned i 15–30 minutter med strømmen slukket. På andre modeller findes en lille rød eller sort reset-knap på undersiden eller bagsiden, som skal trykkes ind.
              </p>
            </>
          ),
        },
        {
          id: 'termostat-fejl',
          title: 'Fejl i den elektroniske styring',
          content: (
            <>
              <p>
                Mange moderne el-radiatorer har digitalt display med dag- og natsænkning. Hvis displayet er slukket eller blinker med en fejlkode, kan det skyldes en defekt printplade eller kondensator i styringen.
              </p>
              <p>
                På ældre modeller med mekanisk drejeknap sidder et bimetalbånd. Med tiden kan kontaktpunkterne sode til og miste evnen til at lede den kraftige strøm igennem til varmelegemet.
              </p>
            </>
          ),
        },
        {
          id: 'udskiftning',
          title: 'Hvornår er el-radiatoren udtjent?',
          content: (
            <>
              <p>
                Hvis varmelegemet er brændt over, eller hvis radiatoren slår HFI/HPFI-relæet fra ved tilslutning, kan det sjældent betale sig at reparere apparatet.
              </p>
              <p>
                En ny energieffektiv el-radiator med digital termostat og åbent-vindue-funktion koster typisk mellem <span className="font-mono">700 og 2.500 kr.</span> og bruger markant mindre standby-energi end udtjente el-paneler fra 1980’erne.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Sikkerhed og krav om autoriseret el-installatør',
        content: (
          <p>
            Faste elinstallationer – herunder el-radiatorer der er tilsluttet i en vægdåse uden almindeligt stik – må kun udskiftes eller repareres af en autoriseret el-installatør. Forsøg aldrig selv at åbne kabinettet på en el-radiator eller rode med de interne ledninger under spænding.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Hvorfor lugter el-radiatoren brændt, når jeg tænder den om efteråret?',
          a: 'Hvis radiatoren har stået slukket hele sommeren, har der lagt sig støv på varmelegemet. Når det opvarmes første gang, brænder støvet af, hvilket afgiver en kortvarig lugt. Sørg for god udluftning de første 20 minutter.',
        },
        {
          q: 'Hvorfor slår el-radiatoren mit HPFI-relæ fra?',
          a: 'Hvis et varmelegeme er tæret eller beskadiget, kan der opstå en strømlækage (fejlstrøm) til radiatorens jordforbindelse. Dette udløser HPFI-relæet for at beskytte mod stød. Radiatoren er defekt og skal udskiftes.',
        },
        {
          q: 'Kan man udlufte en el-radiator?',
          a: 'Nej. En el-radiator indeholder ikke vand fra et centralvarmeanlæg og har ingen luftskrue eller ventilstift. Den opvarmes udelukkende af et indvendigt elektrisk modstandselement.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'overordnet guide til varmeudfald' },
        { to: '/spar-paa-varmen', text: 'Spar på varmen', desc: 'effektiv styring af el- og centralvarme' },
        { to: '/radiator-udskiftning-pris', text: 'Pris på udskiftning af radiator', desc: 'hvad koster nyt udstyr' },
      ]}
      sources={[
        { url: 'https://www.bolius.dk/valg-af-radiator-19095', label: 'Valg af radiator - sådan sikrer du, at du vælger rigtigt' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/spar-paa-varme-og-vand-i-hjemmet', label: 'Normalt varmeforbrug i kWh (+ fjernvarme i MWh) | SparEnergi' },
      ]}
    />
  );
}
